import {
	CONDITIONAL_BLOCK_NODE,
	CONDITIONAL_BRANCH_NODE,
	CONDITIONAL_INLINE_NODE,
	emptyConditionalBlock,
} from '@dealstack/frontend/shared/components/EmailTemplates/extensions/conditionalNodes';
import { LIQUID_VARIABLE_NODE } from '@dealstack/frontend/shared/components/EmailTemplates/extensions/liquidVariableNodes';
import type { Editor } from '@tiptap/core';
import type { EditorState } from '@tiptap/pm/state';
import { NodeSelection } from '@tiptap/pm/state';

export const resolveConditionalBlockPos = (editor: Editor, branchPos: number): number | null => {
	const $pos = editor.state.doc.resolve(branchPos);
	return $pos.parent.type.name === CONDITIONAL_BLOCK_NODE ? $pos.before($pos.depth) : null;
};

export const unwrapConditionalBlock = (editor: Editor, pos: number): void => {
	const node = editor.state.doc.nodeAt(pos);
	if (!node || node.type.name !== CONDITIONAL_BLOCK_NODE) {
		return;
	}
	const ifBranch = node.child(0);
	editor
		.chain()
		.focus(undefined, { scrollIntoView: false })
		.command(({ tr }) => {
			tr.replaceWith(pos, pos + node.nodeSize, ifBranch.content);
			return true;
		})
		.run();
};

export const unwrapConditionalInline = (editor: Editor, pos: number): void => {
	const node = editor.state.doc.nodeAt(pos);
	if (!node || node.type.name !== CONDITIONAL_INLINE_NODE) {
		return;
	}
	editor
		.chain()
		.focus(undefined, { scrollIntoView: false })
		.command(({ tr }) => {
			tr.replaceWith(pos, pos + node.nodeSize, node.content);
			return true;
		})
		.run();
};

export const setNodeAttrsQuietly = (
	editor: Editor,
	pos: number,
	attrs: Record<string, unknown>
): void => {
	const node = editor.state.doc.nodeAt(pos);
	if (!node) {
		return;
	}
	editor.view.dispatch(
		editor.state.tr
			.setNodeMarkup(pos, undefined, { ...node.attrs, ...attrs })
			.setMeta('addToHistory', false)
	);
};

const MENU_NODES = [CONDITIONAL_BRANCH_NODE, CONDITIONAL_INLINE_NODE];

export const openConditionalMenu = (editor: Editor, pos: number | null): void => {
	const tr = editor.state.tr.setMeta('addToHistory', false);
	editor.state.doc.descendants((node, nodePos) => {
		if (!MENU_NODES.includes(node.type.name)) {
			return;
		}
		const menuOpen = nodePos === pos;
		if (Boolean(node.attrs.menuOpen) !== menuOpen) {
			tr.setNodeMarkup(nodePos, undefined, { ...node.attrs, menuOpen });
		}
	});
	if (tr.docChanged) {
		editor.view.dispatch(tr);
	}
};

export const closeConditionalMenus = (editor: Editor): void => openConditionalMenu(editor, null);

export const openConditionalMenuAtSelection = (editor: Editor): void => {
	const { $from } = editor.state.selection;
	for (let depth = $from.depth; depth > 0; depth--) {
		if (MENU_NODES.includes($from.node(depth).type.name)) {
			openConditionalMenu(editor, $from.before(depth));
			return;
		}
	}
};

export const setConditionalBlockAttrs = (
	editor: Editor,
	blockPos: number,
	attrs: Record<string, unknown>
): void => {
	const node = editor.state.doc.nodeAt(blockPos);
	if (!node) {
		return;
	}
	editor
		.chain()
		.focus(undefined, { scrollIntoView: false })
		.command(({ tr }) => {
			tr.setNodeMarkup(blockPos, undefined, { ...node.attrs, ...attrs });
			return true;
		})
		.run();
};

export const setElseBranchHidden = (editor: Editor, branchPos: number, hidden: boolean): void => {
	const branch = editor.state.doc.nodeAt(branchPos);
	if (!branch) {
		return;
	}
	editor
		.chain()
		.focus(undefined, { scrollIntoView: false })
		.command(({ tr }) => {
			tr.setNodeMarkup(branchPos, undefined, { ...branch.attrs, hidden });
			return true;
		})
		.run();
};

export const findConditionalAncestor = (
	state: EditorState
): { pos: number; variable: string; kind: 'block' | 'inline' } | null => {
	const { $from } = state.selection;
	for (let depth = $from.depth; depth >= 0; depth--) {
		const node = $from.node(depth);
		if (
			node.type.name === CONDITIONAL_BLOCK_NODE ||
			node.type.name === CONDITIONAL_INLINE_NODE
		) {
			return {
				pos: $from.before(depth),
				variable: String(node.attrs.variable ?? ''),
				kind: node.type.name === CONDITIONAL_BLOCK_NODE ? 'block' : 'inline',
			};
		}
	}
	if (state.selection instanceof NodeSelection) {
		const node = state.selection.node;
		if (
			node.type.name === CONDITIONAL_BLOCK_NODE ||
			node.type.name === CONDITIONAL_INLINE_NODE
		) {
			return {
				pos: state.selection.from,
				variable: String(node.attrs.variable ?? ''),
				kind: node.type.name === CONDITIONAL_BLOCK_NODE ? 'block' : 'inline',
			};
		}
	}
	return null;
};

const variableInRange = (state: EditorState, from: number, to: number): string | null => {
	let found: string | null = null;
	state.doc.nodesBetween(from, to, (node) => {
		if (found === null && node.type.name === LIQUID_VARIABLE_NODE) {
			found = String(node.attrs.name ?? '');
		}
	});
	return found;
};

export const canMakeSelectionConditional = (state: EditorState): boolean =>
	!state.selection.empty && findConditionalAncestor(state) === null;

const hasVariable = (variable: string): boolean => variable.trim().length > 0;

export type ConditionalSelectionKind = 'block' | 'inline';

export const makeSelectionConditional = (
	editor: Editor,
	fallbackVariable: string
): ConditionalSelectionKind | null => {
	const { state } = editor;
	if (!canMakeSelectionConditional(state)) {
		return null;
	}
	const { $from, $to, from, to } = state.selection;
	const variable = variableInRange(state, from, to) ?? fallbackVariable;
	if (!hasVariable(variable)) {
		return null;
	}

	const singleParagraph = $from.parent === $to.parent;
	const wholeParagraph =
		singleParagraph && $from.parentOffset === 0 && $to.parentOffset === $to.parent.content.size;

	closeConditionalMenus(editor);

	if (singleParagraph && !wholeParagraph) {
		const inlineType = state.schema.nodes[CONDITIONAL_INLINE_NODE];
		if (!inlineType) {
			return null;
		}
		const slice = state.doc.slice(from, to);
		const node = inlineType.create({ variable, elseText: '', menuOpen: true }, slice.content);
		editor
			.chain()
			.focus(undefined, { scrollIntoView: false })
			.command(({ tr }) => {
				tr.replaceWith(from, to, node);
				return true;
			})
			.run();
		return 'inline';
	}

	editor
		.chain()
		.focus(undefined, { scrollIntoView: false })
		.wrapIn(CONDITIONAL_BLOCK_NODE, { variable })
		.run();
	openConditionalMenuAtSelection(editor);
	return 'block';
};

export const insertConditionalBlock = (editor: Editor, variable: string): void => {
	if (!hasVariable(variable)) {
		return;
	}
	closeConditionalMenus(editor);
	editor
		.chain()
		.focus(undefined, { scrollIntoView: false })
		.insertContent(emptyConditionalBlock(variable))
		.run();
};
